import { matchPath } from 'react-router-dom'
import { ROUTES } from '@common/constants/routes'
import { IArcActivity, IArcActivityRef } from '@common/interfaces/arcPresence'
import { INotesData } from '@common/interfaces/notes'
import { select } from 'redux-saga/effects'
import { isDoneWrite } from '../../helpers/arcPresence/arcPresenceStore'
import { putPromise } from '../../helpers/redux'
import { show as showClientInfo } from '../clientInfo/actions'
import { clientInfo as clientInfoSelector } from '../clientInfo/selectors'
import { ICalendarRangeParams, listAgenda, listCalendarRange } from '../dashboard/actions'
import { agenda, calendarRange } from '../dashboard/selectors'
import { listCommentary, listEvents } from '../generalAnalysis/actions'
import { current as generalAnalysisCurrent } from '../generalAnalysis/selectors'
import { listMyTasks, showTask } from '../myTasks/actions'
import {
  myTasksData as myTasksDataSelector,
  lastListParams as myTasksLastListParamsSelector,
  taskDetailsById,
} from '../myTasks/selectors'
import { listNotes } from '../notes/actions'
import {
  notes as notesSelector,
  lastListContext as notesLastListContextSelector,
} from '../notes/selectors'
import { listFavoriteProjects, listProjects, showProject } from '../taskProjects/actions'
import { projects, selectedProject } from '../taskProjects/selectors'
import { arcWriteDomainOf, ArcRefetchDomain } from './arcWriteRefetch'

interface IScopedData {
  data: unknown
  requestedKey: string | null
}

/** The Calendar page's last request, read back off its slice's key. */
export const calendarRangeFromKey = (key: string | null): ICalendarRangeParams | null => {
  const [startDate, endDate, userIds = ''] = key?.split(':') ?? []
  if (!startDate || !endDate) return null
  return { startDate, endDate, userIds: userIds ? userIds.split(',') : [] }
}

/** Both calendars, if loaded, quietly: the Today card on the dashboard and the Calendar page. */
export function* refreshCalendars() {
  const today = (yield select(agenda)) as IScopedData
  if (today.data) {
    yield putPromise(
      listAgenda({ ...(today.requestedKey ? { date: today.requestedKey } : {}), isQuiet: true }),
    )
  }
  const range = (yield select(calendarRange)) as IScopedData
  const rangeParams = calendarRangeFromKey(range.requestedKey)
  if (range.data && rangeParams)
    yield putPromise(listCalendarRange({ ...rangeParams, isQuiet: true }))
}

const namesId = (refs: IArcActivityRef[] | null, id: string | undefined): boolean =>
  !!id && !!refs?.some(({ value }) => value === id)

function* refreshProjects(refs: IArcActivityRef[] | null) {
  yield putPromise(listFavoriteProjects())
  const loadedProjects = (yield select(projects)) as { data: unknown }
  if (loadedProjects.data) yield putPromise(listProjects())
  const openProject = (yield select(selectedProject)) as { data: { id?: string } | null }
  const openId = openProject.data?.id
  if (openId && namesId(refs, openId)) yield putPromise(showProject(openId))
}

function* refreshTasks(refs: IArcActivityRef[] | null) {
  const tasks = (yield select(myTasksDataSelector)) as { data?: unknown } | null
  const lastParams = (yield select(myTasksLastListParamsSelector)) as object | null
  if (tasks?.data && lastParams) {
    yield putPromise(listMyTasks({ ...lastParams, loadMore: false, skipLoader: true }))
  }
  const loadedTasks = (yield select(taskDetailsById)) as Record<string, unknown>
  for (const taskId of Object.keys(loadedTasks)) {
    if (namesId(refs, taskId)) yield putPromise(showTask(taskId))
  }
}

function* refreshNotes() {
  const loadedNotes = (yield select(notesSelector)) as INotesData | null
  const ctx = (yield select(notesLastListContextSelector)) as {
    id?: string
    params?: object
  } | null
  if (loadedNotes && ctx?.id) {
    yield putPromise(listNotes(ctx.id, { ...ctx.params, loadMore: false, skipLoader: true }))
  }
}

/**
 * The amendment-analysis commentary panel. A 'notes' write refreshes it too: Arc
 * often posts a commentary reply through the generic notes `Add`/`Update`, and the
 * panel reads the generalAnalysis slice, not the notes one. The Deal Summary tab
 * has no analysisId in its URL, so the loaded write-up's id stands in there.
 */
function* refreshCommentary() {
  const current = (yield select(generalAnalysisCurrent)) as { id?: string } | null
  const { pathname } = window.location
  const amendmentMatch = matchPath({ path: ROUTES.AMENDMENT_ANALYSIS }, pathname)
  const summaryMatch = matchPath({ path: ROUTES.PROSPECT_SUMMARY_PAGE }, pathname)
  const analysisId = amendmentMatch?.params?.analysisId ?? (summaryMatch ? current?.id : undefined)
  if (analysisId && current?.id === analysisId) {
    yield putPromise(listCommentary(analysisId, { skipLoader: true }))
    yield putPromise(listEvents(analysisId, { skipLoader: true }))
  }
}

function* refreshClientInfo() {
  const loadedClient = (yield select(clientInfoSelector)) as { id?: string } | null
  if (loadedClient?.id) yield putPromise(showClientInfo(loadedClient.id))
}

/**
 * One domain's data on screen, re-read quietly. Only what is loaded is fetched.
 * `refs` are the records a single write named; a whole-turn refresh has none, so
 * the open task or project panel is left alone then.
 */
export function* refreshArcDomain(domain: ArcRefetchDomain, refs: IArcActivityRef[] | null) {
  if (domain === 'commentary' || domain === 'notes') yield* refreshCommentary()
  if (domain === 'notes') yield* refreshNotes()
  if (domain === 'clientInfo') yield* refreshClientInfo()
  if (domain === 'tasks') yield* refreshTasks(refs)
  if (domain === 'calendar') yield* refreshCalendars()
  if (domain === 'projects') yield* refreshProjects(refs)
}

const DUPLICATE_WINDOW_MS = 60 * 1000
/** Per turn, the domains already refreshed for it, and when that turn was first seen. */
const refreshedTurnDomains = new Map<string, { at: number; domains: Set<string> }>()

const forgetOldTurns = (now: number) =>
  refreshedTurnDomains.forEach(({ at }, id) => {
    if (now - at > DUPLICATE_WINDOW_MS) refreshedTurnDomains.delete(id)
  })

/** Record that a turn's domain was refreshed, as one of its writes landed. */
export const noteDomainRefreshed = (turnId: string, domain: string, now: number): void => {
  forgetOldTurns(now)
  const entry = refreshedTurnDomains.get(turnId) ?? { at: now, domains: new Set<string>() }
  entry.domains.add(domain)
  refreshedTurnDomains.set(turnId, entry)
}

/**
 * True the first time a turn's domain is claimed. A session member hears one turn
 * as ARC_ACTIVITY per write, ATLAS_AI_COMPLETE and CHANNEL_ARC_WROTE; whichever
 * whole-turn event comes after the domain was refreshed is skipped for it.
 */
export const claimTurnDomain = (turnId: string, domain: string, now: number): boolean => {
  forgetOldTurns(now)
  if (refreshedTurnDomains.get(turnId)?.domains.has(domain)) return false
  noteDomainRefreshed(turnId, domain, now)
  return true
}

/**
 * The screen catches up with a change Arc made as it lands, rather than when its
 * whole reply does. Every write refreshes, so a turn that changes a task twice
 * shows both; the turn's own completion then skips what was already refreshed.
 */
export function* refreshAfterArcWrite(activity: IArcActivity) {
  if (!isDoneWrite(activity)) return
  const domain = arcWriteDomainOf(activity.service)
  if (!domain) return
  if (activity.chatHistoryId) noteDomainRefreshed(activity.chatHistoryId, domain, Date.now())
  yield* refreshArcDomain(domain, activity.refs)
}
